import React from 'react';
import styled from 'styled-components';
import { Flex, Box, Text } from 'rebass/styled-components';
import FamilyIcon from '../../images/family-icon.svg';
import DoctorIcon from '../../images/doctor-icon.svg';
import Bellicon from '../../images/bell-icon.svg';
import CartIcon from '../../images/cart-icon.svg';
import PillIcon from '../../images/medicine-icon.svg';
import ShieldIcon from '../../images/security-icon.svg';

const ServiceSection = styled.section`
  max-width: 1300px;
  margin: 120px auto 200px auto;
  padding: 0 20px;
  font-family: 'Sk-Modernist-Regular', sans-serif;
  color: #1a232e;

  @media (max-width: 768px) {
    margin: 80px auto;
  }
`;

const IntroFlex = styled(Flex)`
  justify-content: space-between;
  align-items: flex-start;
  gap: 40px;
  margin-bottom: 70px;

  @media (max-width: 900px) {
    flex-direction: column;
    gap: 24px;
    margin-bottom: 50px;
  }
`;

const MainHeading = styled.h2`
  color: #ff4f00;
  font-weight: 400;
  line-height: 1.1;
  max-width: 650px;
  margin: 0;

  font-size: 64px;

  @media (max-width: 1024px) {
    font-size: 48px;
  }

  @media (max-width: 768px) {
    font-size: 36px;
  }

  @media (max-width: 480px) {
    font-size: 30px;
  }
`;

const IntroDescription = styled(Text)`
  font-size: 24px;
  line-height: 1.6;
  color: #51504e;
  max-width: 450px;

  @media (max-width: 768px) {
    font-size: 18px;
    max-width: 100%;
  }
`;

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 32px;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const FeatureCard = styled(Box)`
  background: ${(props) =>
    props.isHighlighted ? 'radial-gradient(circle at 0% 20%,#FFFDFD 15%,#FFEDE6 50%, #FFFDFD 100%)' : 'white'};

  border-radius: 12px;
  padding: 30px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;

  @media (max-width: 768px) {
    padding: 24px;
  }
`;

const IconWrapper = styled(Flex)`
  margin-bottom: 20px;

  img {
    width: 32px;
    height: 32px;
  }
`;

const FeatureTitle = styled.h3`
  font-size: 24px;
  font-weight: 400;
  line-height: 32px;
  margin-bottom: 14px;
  color: #100f0e;

  @media (max-width: 768px) {
    font-size: 20px;
  }
`;

const FeatureText = styled(Text)`
  font-size: 16px;
  line-height: 1.6;
  color: #252422;

  @media (max-width: 768px) {
    font-size: 15px;
  }
`;

const featuresData = [
  {
    icon: FamilyIcon,
    title: 'Manage family members',
    description: 'Add loved ones and manage their medicines in one secure account.',
    isHighlighted: false,
  },
  {
    icon: DoctorIcon,
    title: 'Connected to your pharmacy and GP practice',
    description: 'Book appointments, send messages and stay connected in one place.',
    isHighlighted: true,
  },
  {
    icon: Bellicon,
    title: 'Medication reminders',
    description: 'Set daily reminders so you never miss a dose.',
    isHighlighted: false,
  },
  {
    icon: CartIcon,
    title: 'Simple reordering',
    description: 'Order your repeat medicines in just a few clicks.',
    isHighlighted: true,
  },
  {
    icon: PillIcon,
    title: 'Never run out of medication',
    description: 'Get reminders when it is time to reorder.',
    isHighlighted: false,
  },
  {
    icon: ShieldIcon,
    title: 'Your data is safe',
    description: 'Your information is encrypted and never shared with third parties.',
    isHighlighted: true,
  },
];

const Services = () => {
  return (
    <ServiceSection>
      <IntroFlex>
        <MainHeading>A free service from your pharmacy.</MainHeading>

        <IntroDescription>
          MyPharmacy is a free service designed to make managing your medicines easier and more convenient.
        </IntroDescription>
      </IntroFlex>

      <FeaturesGrid>
        {featuresData.map((feature, index) => (
          <FeatureCard key={index} isHighlighted={feature.isHighlighted}>
            <IconWrapper>
              <feature.icon size={32} />
            </IconWrapper>

            <FeatureTitle>{feature.title}</FeatureTitle>

            <FeatureText>{feature.description}</FeatureText>
          </FeatureCard>
        ))}
      </FeaturesGrid>
    </ServiceSection>
  );
};

export default Services;
